<template>
  <nav
    v-if="currentWorkspace"
    :key="`workspacemenu-${currentWorkspace.id}`"
    class="workspacefolders-nav"
  >
    <workspace-folders-list
      :organization-slug="organizationSlug"
      :team-slug="teamSlug"
      :folder-slug="folderSlug"
    />

    <!-- SHARED and EXAMPLE MODELS -->
    <div v-show="currentWorkspace.isPersonal">
      <hr class="mt-0" />
      <router-link
        :to="{ name: 'shared-models' }"
        class="workspacefolders-nav__entry workspacefolders-nav__link"
      >
        <lucide-icon
          name="shared-models"
          class="mr-3 text-19"
          style="margin-bottom: -5px"
        />
        <span>{{ $t("labels.sharedModels") }}</span>

        <limits-badge type="canShare" style="right: 1rem;" />
      </router-link>

      <hr />

      <router-link
        :to="{ name: 'public-models' }"
        class="workspacefolders-nav__entry workspacefolders-nav__link"
      >
        <lucide-icon
          name="public-models"
          class="mr-3 text-19"
          style="margin-bottom: -5px"
        />
        <span>{{ $t("labels.exampleModels") }}</span>
      </router-link>

      <hr />
    </div>
  </nav>
</template>

<script setup lang="ts">
import { useWorkspaces } from "@/api/workspaces.store";

import LucideIcon from "@core/ui/LucideIcon.vue";
import LimitsBadge from "@core/ui/LimitsBadge.vue";
import WorkspaceFoldersList from "./WorkspaceFoldersList.vue";

interface Props {
  organizationSlug?: string;
  teamSlug?: string;
  folderSlug?: string;
}
const props = withDefaults(defineProps<Props>(), {
  organizationSlug: undefined,
  teamSlug: undefined,
  folderSlug: undefined
});

const { currentWorkspace } = useWorkspaces(props);
</script>

<style lang="scss">
.workspacefolders-nav {
  height: 100%;
  width: 100%;
  min-height: 0;
  overflow: auto;

  &__entry {
    position: relative;
    display: block;

    background-color: transparent;
    border-left: 5px solid transparent;
    transition: border-color 200ms;

    &.-nobreak {
      white-space: nowrap;
    }

    &:hover {
      background-color: var(--color-background);
      //border-left-color: var(--color-action);
      text-shadow: 0 1px 2px var(--color-shadow-1);
    }

    &.is-active {
      border-left-color: var(--color-action);
      background-color: var(--color-background);
      color: var(--color-action);
      text-shadow: 0 1px 2px var(--color-shadow-1);
    }
    &.-drag-target {
      border-left-color: var(--color-action);
      outline: 2px solid var(--color-action);
      outline-offset: -2px;
      color: var(--color-action);
    }

    &-block {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(0, 2.5rem);
    }

    &-button {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 0.5rem;
      background-color: transparent;
      border: none;
      color: var(--color-text-light);
      opacity: 0;
      transition:
        color 300ms,
        opacity 200ms,
        background-color 300ms,
        border-radius 300ms;
      margin: 5px;
      margin-left: 0;
      max-height: 2rem;
      &:hover,
      &:focus-visible {
        color: var(--color-action);
        //filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
        box-shadow: 0 1px 3px var(--color-shadow-1);
        border-radius: 5px;
        background-color: var(--color-background-deeper);
      }
      &:focus-visible {
        outline: 2px solid var(--color-action);
      }
      &:active {
        box-shadow: inset 0 1px 3px var(--color-shadow-2);
      }
      &:focus {
        opacity: 1;
      }
    }
    &:hover &-button,
    &.is-active &-button {
      opacity: 1;
    }
  }
  &__link {
    display: block;
    padding: 0.5rem 1rem;
    color: var(--color-text);
    user-select: none;
    overflow-wrap: break-word;
    hyphens: auto;
    &:focus-visible {
      color: var(--color-action);
      outline: 2px solid var(--color-action);
      outline-offset: -2px;
      border-radius: 5px;
    }
  }
  &__entry:hover &__link,
  &__entry.is-active &__link {
    color: var(--color-action);
  }
}

.with-shadow {
  box-shadow: 0 1px 10px var(--color-shadow-1);
}
</style>
